import { 
  GraduationCap, 
  Shield, 
  Mail, 
  MapPin, 
  Phone, 
  Heart,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  ArrowRight,
  CheckCircle,
  Lock,
  Server,
  Database,
  Eye,
  Cookie,
  Globe,
  Users,
  FileText,
  Clock,
  AlertCircle
} from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPage = () => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2"
            >
              <div className="flex items-center gap-0 font-extrabold">
                <span className="font-bold text-gray-900 text-[1.8rem]">
                  Learn
                </span>
                <span className="font-bold text-orange-500 text-[1.8rem]">
                  Hub
                </span>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="text-sm text-gray-600 transition-colors hover:text-primary"
              >
                Home
              </Link>
              <Link
                to="/terms"
                className="text-sm text-gray-600 transition-colors hover:text-primary"
              >
                Terms
              </Link>
              <Link
                to="/contact"
                className="text-sm text-gray-600 transition-colors hover:text-primary"
              >
                Contact
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl px-4 py-8 mx-auto sm:px-6 lg:px-8 md:py-12">
        {/* Hero Section */}
        <div className="mb-8 text-center md:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full mb-4">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium text-primary">
              Your Privacy Matters
            </span>
          </div>
          <h1 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl">
            Privacy Policy
          </h1>
          <p className="max-w-2xl mx-auto text-gray-500">
            We are committed to protecting your personal information and being
            transparent about how we use it
          </p>
          <p className="flex items-center justify-center gap-1 mt-3 text-sm text-gray-400">
            <Clock className="w-3 h-3" />
            Last updated: April 2026
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3 mb-8 md:grid-cols-4 md:mb-12">
          <div className="p-3 text-center bg-white border border-gray-200 shadow-sm rounded-xl">
            <Shield className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-xs font-semibold text-gray-900">256-bit SSL</p>
            <p className="text-[10px] text-gray-400">Encryption</p>
          </div>
          <div className="p-3 text-center bg-white border border-gray-200 shadow-sm rounded-xl">
            <Lock className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-xs font-semibold text-gray-900">
              GDPR Compliant
            </p>
            <p className="text-[10px] text-gray-400">Data Protection</p>
          </div>
          <div className="p-3 text-center bg-white border border-gray-200 shadow-sm rounded-xl">
            <Database className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-xs font-semibold text-gray-900">
              Secure Storage
            </p>
            <p className="text-[10px] text-gray-400">Encrypted Data</p>
          </div>
          <div className="p-3 text-center bg-white border border-gray-200 shadow-sm rounded-xl">
            <Eye className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-xs font-semibold text-gray-900">Transparent</p>
            <p className="text-[10px] text-gray-400">Full Disclosure</p>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
          {/* Decorative top bar */}
          <div className="h-1.5 bg-gradient-to-r from-primary via-orange-500 to-primary"></div>

          <div className="p-6 md:p-8 lg:p-10">
            <div className="prose-sm prose text-gray-600 max-w-none">
              {/* Introduction */}
              <div className="p-4 mb-8 border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-blue-100 rounded-xl">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="mb-1 text-lg font-semibold text-gray-900">
                      Your Privacy is Our Priority
                    </h2>
                    <p className="text-sm text-gray-600">
                      At LearnHub, we are committed to protecting your privacy
                      and ensuring the security of your personal information.
                      This policy explains how we collect, use, and safeguard
                      your data when you use our platform.
                    </p>
                  </div>
                </div>
              </div>

              {/* 1. Introduction */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                    <FileText className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    1. Introduction
                  </h2>
                </div>
                <p className="mb-3 text-sm leading-relaxed">
                  Welcome to LearnHub ("we," "us," or "our"). This Privacy
                  Policy explains how we collect, use, disclose, and safeguard
                  your information when you use our learning platform, including
                  our website, mobile applications, and all associated services
                  (collectively, the "Platform").
                </p>
                <p className="text-sm leading-relaxed">
                  By using the Platform, you consent to the data practices
                  described in this policy. If you do not agree to this policy,
                  please do not use our Platform.
                </p>
              </div>

              {/* 2. Information We Collect */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                    <Database className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    2. Information We Collect
                  </h2>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h3 className="flex items-center gap-2 mb-2 font-semibold text-gray-800">
                      <Users className="w-4 h-4 text-primary" />
                      Personal Information
                    </h3>
                    <ul className="grid grid-cols-1 gap-2 text-sm text-gray-600 md:grid-cols-2">
                      <li className="flex items-center gap-2">
                        • Account Information (name, email, username)
                      </li>
                      <li className="flex items-center gap-2">
                        • Profile picture and bio
                      </li>
                      <li className="flex items-center gap-2">
                        • Contact information (phone, address - optional)
                      </li>
                      <li className="flex items-center gap-2">
                        • Demographic information
                      </li>
                      <li className="flex items-center gap-2">
                        • Payment information (processed securely)
                      </li>
                      <li className="flex items-center gap-2">
                        • Social media profiles (if linked)
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h3 className="flex items-center gap-2 mb-2 font-semibold text-gray-800">
                      <Server className="w-4 h-4 text-primary" />
                      Automatically Collected Information
                    </h3>
                    <ul className="grid grid-cols-1 gap-2 text-sm text-gray-600 md:grid-cols-2">
                      <li className="flex items-center gap-2">
                        • Device information and browser type
                      </li>
                      <li className="flex items-center gap-2">
                        • IP address and location data
                      </li>
                      <li className="flex items-center gap-2">
                        • Usage patterns and page views
                      </li>
                      <li className="flex items-center gap-2">
                        • Cookies and tracking data
                      </li>
                      <li className="flex items-center gap-2">
                        • Log data and error reports
                      </li>
                      <li className="flex items-center gap-2">
                        • Referral sources
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h3 className="flex items-center gap-2 mb-2 font-semibold text-gray-800">
                      Learning Data
                    </h3>
                    <ul className="grid grid-cols-1 gap-2 text-sm text-gray-600 md:grid-cols-2">
                      <li className="flex items-center gap-2">
                        • Course progress and completion
                      </li>
                      <li className="flex items-center gap-2">
                        • Quiz and assessment scores
                      </li>
                      <li className="flex items-center gap-2">
                        • Certificate records
                      </li>
                      <li className="flex items-center gap-2">
                        • Mentoring session history
                      </li>
                      <li className="flex items-center gap-2">
                        • Forum posts and comments
                      </li>
                      <li className="flex items-center gap-2">
                        • Feedback and reviews
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 3. How We Use Your Information */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                    <Eye className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    3. How We Use Your Information
                  </h2>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="p-3 border border-green-100 bg-green-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-gray-800">
                        Providing Services
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Account management, course delivery, progress tracking,
                      certificates, and support
                    </p>
                  </div>
                  <div className="p-3 border border-blue-100 bg-blue-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-gray-800">
                        Communication
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Updates, notifications, responses to inquiries, and
                      announcements
                    </p>
                  </div>
                  <div className="p-3 border border-purple-100 bg-purple-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-semibold text-gray-800">
                        Improvement & Analytics
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Usage analysis, feature improvements, debugging, and
                      research
                    </p>
                  </div>
                  <div className="p-3 border border-orange-100 bg-orange-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-orange-600" />
                      <span className="text-sm font-semibold text-gray-800">
                        Security & Compliance
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Identity verification, fraud prevention, legal compliance,
                      and rights protection
                    </p>
                  </div>
                </div>
              </div>

              {/* 4. Information Sharing */}
              <div className="p-4 mb-8 border bg-amber-50 rounded-xl border-amber-100">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-amber-100 rounded-xl">
                    <Shield className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="mb-1 text-lg font-semibold text-gray-900">
                      4. Information Sharing
                    </h2>
                    <p className="mb-2 text-sm font-medium text-gray-700">
                      We Do NOT Sell Your Data
                    </p>
                    <p className="text-sm text-gray-600">
                      We do not sell, rent, or trade your personal information
                      to third parties for their marketing purposes. Your trust
                      is our most valuable asset.
                    </p>
                  </div>
                </div>
              </div>

              {/* 5. Cookies */}
              <div className="p-4 mb-8 border border-gray-200 bg-gray-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-gray-200 rounded-xl">
                    <Cookie className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="mb-1 text-lg font-semibold text-gray-900">
                      5. Cookies and Tracking
                    </h2>
                    <p className="mb-2 text-sm text-gray-600">
                      We use cookies to enhance your experience, analyze usage,
                      and remember preferences.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg">
                        Essential
                      </span>
                      <span className="px-2 py-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg">
                        Analytics
                      </span>
                      <span className="px-2 py-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg">
                        Preferences
                      </span>
                      <span className="px-2 py-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg">
                        Marketing (with consent)
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. Data Security */}
              <div className="p-4 mb-8 border border-green-100 bg-green-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-green-100 rounded-xl">
                    <Lock className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="mb-1 text-lg font-semibold text-gray-900">
                      6. Data Security
                    </h2>
                    <p className="text-sm text-gray-600">
                      We implement enterprise-grade security measures including
                      SSL/TLS encryption, secure databases, access controls,
                      regular audits, and employee training to protect your
                      data.
                    </p>
                  </div>
                </div>
              </div>

              {/* 7. Your Rights */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                    <Users className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    7. Your Rights
                  </h2>
                </div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  <div className="p-3 text-center bg-gray-50 rounded-xl">
                    <span className="block mb-1 text-lg">📋</span>
                    <p className="text-xs font-medium text-gray-800">Access</p>
                    <p className="text-[10px] text-gray-500">View your data</p>
                  </div>
                  <div className="p-3 text-center bg-gray-50 rounded-xl">
                    <span className="block mb-1 text-lg">✏️</span>
                    <p className="text-xs font-medium text-gray-800">
                      Correction
                    </p>
                    <p className="text-[10px] text-gray-500">
                      Fix inaccuracies
                    </p>
                  </div>
                  <div className="p-3 text-center bg-gray-50 rounded-xl">
                    <span className="block mb-1 text-lg">🗑️</span>
                    <p className="text-xs font-medium text-gray-800">
                      Deletion
                    </p>
                    <p className="text-[10px] text-gray-500">Erase your data</p>
                  </div>
                  <div className="p-3 text-center bg-gray-50 rounded-xl">
                    <span className="block mb-1 text-lg">📤</span>
                    <p className="text-xs font-medium text-gray-800">
                      Portability
                    </p>
                    <p className="text-[10px] text-gray-500">
                      Export your data
                    </p>
                  </div>
                  <div className="p-3 text-center bg-gray-50 rounded-xl">
                    <span className="block mb-1 text-lg">🔕</span>
                    <p className="text-xs font-medium text-gray-800">Opt-Out</p>
                    <p className="text-[10px] text-gray-500">
                      Marketing emails
                    </p>
                  </div>
                  <div className="p-3 text-center bg-gray-50 rounded-xl">
                    <span className="block mb-1 text-lg">⚖️</span>
                    <p className="text-xs font-medium text-gray-800">
                      Withdraw Consent
                    </p>
                    <p className="text-[10px] text-gray-500">Anytime</p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="pt-6 mt-8 border-t border-gray-200">
                <div className="p-5 bg-gradient-to-r from-primary/5 to-orange-500/5 rounded-xl">
                  <h2 className="flex items-center gap-2 mb-3 text-lg font-semibold text-gray-900">
                    <Mail className="w-5 h-5 text-primary" />
                    Contact Us
                  </h2>
                  <p className="mb-4 text-sm text-gray-600">
                    Have questions about this policy or want to exercise your
                    rights? Contact our privacy team:
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4 text-primary" />
                      <span>ndzelenromarick@gmail.com</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4 text-primary" />
                      <span>+237 676 154 253</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span>Bamenda, Cameroon</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Light Footer */}
      <footer className="mt-12 bg-white border-t border-gray-200">
        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">© {currentYear}</span>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
              <Link
                to="/privacy"
                className="transition-colors hover:text-primary"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="transition-colors hover:text-primary"
              >
                Terms
              </Link>
              <Link
                to="/contact"
                className="transition-colors hover:text-primary"
              >
                Contact
              </Link>
              <Link
                to="/about"
                className="transition-colors hover:text-primary"
              >
                About
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Missing component
const TrendingUp = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
    <polyline points="17 18 23 18 23 12"></polyline>
  </svg>
);

export default PrivacyPage;